import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../styles/DictionaryForm.css";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import { dictionaryService } from "../../services/api";
import { AuthProvider, useAuth } from "../../context/AuthContext";

// Schéma de validation avec Yup
const EntrySchema = Yup.object().shape({
  word: Yup.string().required("Le mot est requis"),
  definition: Yup.string().required("La définition est requise"),
  word_type: Yup.string().required("Le type de mot est requis"),
  region: Yup.string().required("La région est requise"),
});

const DictionaryForm = () => {
  const { currentUser } = useAuth();
  const { id } = useParams(); // Si on édite, on a un id
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    word: "",
    translation: "",
    signification: "",
    arabic_word: "",
    word_type: "",
    region: "",
    details: "",
    created_by: currentUser.id,
  });
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Si édition, charger les données existantes
  useEffect(() => {
    if (id) {
      const fetchEntry = async () => {
        try {
          const response = await dictionaryService.getEntry(id);
          setInitialValues({
            word: response.data.word,
            signification: response.data.signification,
            arabic_word: response.data.arabic_word,
            details: response.data.details.example,
            created_by: response.data.created_by,
            translation: response.data.translation,
            word_type: response.data.word_type,
            region: response.data.region,
          });
        } catch (err) {
          setError("Impossible de charger l'entrée.");
        } finally {
          setLoading(false);
        }
      };
      fetchEntry();
    }
  }, [id]);

  // Soumission du formulaire
  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("Form values:", values);
    setError(null);
    setSuccess(false);
    try {
      const update_data = {
        word: values.word,
        signification: values.signification,
        arabic_word: values.arabic_word,
        details: { example: values.details },
        created_by: currentUser.id,
        translation: values.translation,
        word_type: values.word_type,
        region: values.region,
      };
      if (id) {
        // Modification
        const res = await dictionaryService.updateEntry(id, update_data);
        if (res.status === 201) {
          console.log("Entry created successfully:", res.data);
          navigate(`/dictionary/${id}`);
        }
        setSuccess(true);
      } else {
        // Ajout
        const res = await dictionaryService.createEntry(update_data);
        if (res.status === 201) {
          console.log("Entry created successfully:", res.data);
          const new_id = res.data.id;
          navigate(`/dictionary/${new_id}`);
        }
        setSuccess(true);
      }
    } catch (err) {
      setError(
        "Erreur lors de l'enregistrement. Veuillez vérifier les champs."
      );
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Container className="dictionary-form-container">
        <div className="text-center">
          <Spinner animation="border" className="spinner-border" />
        </div>
      </Container>
    );
  }

  return (
    <div>
      <Container className="dictionary-form-container">
        <Card>
          <Card.Header>
            <h3>{id ? "Modifier" : "Ajouter"} une entrée</h3>
          </Card.Header>
          <Card.Body>
            {error && <Alert variant="danger" className="alert alert-danger">{error}</Alert>}
            {success && (
              <Alert variant="success" className="alert alert-success">Enregistré avec succès !</Alert>
            )}
            <Formik
              initialValues={initialValues}
              // validationSchema={EntrySchema}
              enableReinitialize
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <FormikForm>
                  <div className="form-grid">
                    <div className="form-group-compact">
                      <label htmlFor="word" className="form-label">
                        Mot
                      </label>
                      <Field type="text" name="word" className="form-control" />
                      <ErrorMessage
                        name="word"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                    
                    <div className="form-group-compact">
                      <label htmlFor="translation" className="form-label">
                        Traduction
                      </label>
                      <Field
                        type="text"
                        name="translation"
                        className="form-control"
                      />
                      <ErrorMessage
                        name="translation"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                    
                    <div className="form-group-compact">
                      <label htmlFor="word_type" className="form-label">
                        Type de mot
                      </label>
                      <Field as="select" name="word_type" className="form-select">
                        <option value="">Sélectionner</option>
                        <option value="noun">Nom</option>
                        <option value="verb">Verbe</option>
                        <option value="adjective">Adjectif</option>
                        <option value="adverb">Adverbe</option>
                      </Field>
                      <ErrorMessage
                        name="word_type"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                    
                    <div className="form-group-compact">
                      <label htmlFor="region" className="form-label">
                        Région
                      </label>
                      <Field as="select" name="region" className="form-select">
                        <option value="">Sélectionner</option>
                        <option value="centre">Centre</option>
                        <option value="est">Est</option>
                        <option value="ouest">Ouest</option>
                        <option value="sud">Sud</option>
                      </Field>
                      <ErrorMessage
                        name="region"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                    
                    <div className="form-group-compact form-grid-full">
                      <label htmlFor="signification" className="form-label">
                        Définition
                      </label>
                      <Field
                        as="textarea"
                        name="signification"
                        className="form-control"
                      />
                      <ErrorMessage
                        name="signification"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                    
                    <div className="form-group-compact">
                      <label htmlFor="arabic_word" className="form-label">
                        Mot arabe
                      </label>
                      <Field
                        as="textarea"
                        name="arabic_word"
                        className="form-control"
                      />
                      <ErrorMessage
                        name="arabic_word"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                    
                    <div className="form-group-compact">
                      <label htmlFor="details" className="form-label">
                        Exemple
                      </label>
                      <Field
                        as="textarea"
                        name="details"
                        className="form-control"
                      />
                      <ErrorMessage
                        name="details"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    className="btn-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? "Enregistrement..."
                      : id
                      ? "Modifier"
                      : "Ajouter"}
                  </Button>
                </FormikForm>
              )}
            </Formik>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default DictionaryForm;